import { useState } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
} from "react-router-dom";
import { GoogleOAuthProvider } from "@react-oauth/google";
import { AuthProvider } from "./contexts/AuthContext";
import { ThemeProvider } from "./contexts/ThemeContext";
import { CaseHistoryProvider, CaseHistory } from "./contexts/CaseHistoryContext";
import { ProtectedRoute } from "./components/ProtectedRoute";
import { Header } from "./components/Header";
import { Sidebar } from "./components/Sidebar";
import { LoginPage } from "./pages/LoginPage";
import { SignupPage } from "./pages/SignupPage";
import { DashboardPage } from "./pages/DashboardPage";
import { PricingPage } from "./pages/PricingPage";
import { FAQPage } from "./pages/FAQPage";
import { ProfileSettingsPage } from "./pages/ProfileSettingsPage";
import { PreferencesPage } from "./pages/PreferencesPage";

const GOOGLE_CLIENT_ID = import.meta.env.VITE_GOOGLE_CLIENT_ID || "";

function AppLayout() {
  const [selectedCase, setSelectedCase] = useState<CaseHistory | null>(null);
  const [resetKey, setResetKey] = useState(0);

  const handleNewCase = () => {
    setSelectedCase(null);
    setResetKey((prev) => prev + 1);
  };

  const handleSelectCase = (caseData: CaseHistory) => {
    setSelectedCase(caseData);
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-slate-900 flex flex-col transition-colors duration-200">
      <Header />
      <div className="flex-1 flex">
        <Sidebar
          onNewCase={handleNewCase}
          onSelectCase={handleSelectCase}
          selectedCaseId={selectedCase?.id}
        />
        <main className="flex-1 overflow-y-auto">
          <div className="container mx-auto px-4 py-8">
            <DashboardPage
              key={resetKey}
              selectedCase={selectedCase}
              onCaseComplete={() => setSelectedCase(null)}
            />
          </div>
        </main>
      </div>
    </div>
  );
}

function App() {
  return (
    <GoogleOAuthProvider clientId={GOOGLE_CLIENT_ID}>
      <AuthProvider>
        <ThemeProvider>
          <CaseHistoryProvider>
            <Router>
            <Routes>
              {/* Public routes */}
              <Route path="/login" element={<LoginPage />} />
              <Route path="/signup" element={<SignupPage />} />
              <Route path="/pricing" element={<PricingPage />} />
              <Route path="/faq" element={<FAQPage />} />

              {/* Protected routes */}
              <Route
                path="/"
                element={
                  <ProtectedRoute>
                    <AppLayout />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/profile"
                element={
                  <ProtectedRoute>
                    <ProfileSettingsPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/preferences"
                element={
                  <ProtectedRoute>
                    <PreferencesPage />
                  </ProtectedRoute>
                }
              />

              {/* Catch-all redirect */}
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
            </Router>
          </CaseHistoryProvider>
        </ThemeProvider>
      </AuthProvider>
    </GoogleOAuthProvider>
  );
}

export default App;
